
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import FeaturesSection from './components/FeaturesSection';
import NewsSection from './components/NewsSection';
import ProductsSection from './components/ProductsSection';
import TestimonialsSection from './components/TestimonialsSection';
import FAQSection from './components/FAQSection';
import CTASection from './components/CTASection';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const homeJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'おかやまネジ製作所',
    url: siteUrl,
    description: '岡山市の精密ネジ製造。創業50年の技術で特注ネジ・チタン加工・1本から対応。',
    inLanguage: 'ja',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#business`,
    name: 'おかやまネジ製作所',
    description: '岡山市の精密ネジ製造。創業50年の技術で特注ネジ・チタン加工・1本から対応。設計者様の困ったを解決します。',
    url: siteUrl,
    telephone: '+81-86-012-3456',
    faxNumber: '+81-86-012-3457',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '北区○○町1-2-3',
      addressLocality: '岡山市',
      addressRegion: '岡山県',
      postalCode: '700-0000',
      addressCountry: 'JP',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 34.6617,
      longitude: 133.935,
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
    foundingDate: '1976',
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 5 },
    priceRange: '$$',
    areaServed: [
      { '@type': 'City', name: '岡山市' },
      { '@type': 'City', name: '倉敷市' },
      { '@type': 'City', name: '総社市' },
      { '@type': 'City', name: '津山市' },
    ],
    knowsAbout: ['特注ネジ製造', '難削材加工', 'チタン加工', 'ステンレス加工', '精密加工', 'CNC旋盤加工'],
    hasMap: 'https://www.google.com/maps?q=34.6617,133.9350',
    currenciesAccepted: 'JPY',
    paymentAccepted: '銀行振込',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '1本からでも注文できますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'はい、1本からでも対応可能です。試作品や少量生産にも柔軟に対応いたします。岡山市近隣の設計担当者様からの小ロット依頼も多数お受けしております。お気軽にご相談ください。',
        },
      },
      {
        '@type': 'Question',
        name: '納期はどのくらいかかりますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '製品の仕様や数量によって異なりますが、通常は1週間〜2週間程度です。お急ぎの場合は短納期対応も可能ですので、まずはご相談ください。',
        },
      },
      {
        '@type': 'Question',
        name: 'どのような材質に対応していますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'チタン、ステンレス、インコネル、真鍮、アルミニウムなど、様々な材質に対応しています。特に難削材の加工経験が豊富です。',
        },
      },
      {
        '@type': 'Question',
        name: '図面がなくても相談できますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'はい、図面がない場合でもご相談ください。現物やスケッチからでも対応可能です。創業50年の経験を活かし、お客様のニーズに合わせた最適な提案をさせていただきます。',
        },
      },
      {
        '@type': 'Question',
        name: '見積もりは無料ですか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'はい、お見積もりは無料です。図面やご要望をお送りいただければ、迅速にお見積もりをご提示いたします。',
        },
      },
      {
        '@type': 'Question',
        name: '品質保証はありますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'はい、すべての製品について厳格な品質管理を行っております。0.01mm単位の精度を保証し、検査成績書の発行も可能です。',
        },
      },
    ],
  },
];

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = '岡山市の特注ネジ製造｜0.01mm精度の難削材加工｜おかやまネジ製作所';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '岡山市の精密ネジ製造。創業50年の技術で特注ネジ・チタン加工・1本から対応。設計者様の困ったを解決します。短納期・高精度｜086-012-3456');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', siteUrl + '/');

    const scriptId = 'home-jsonld';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(homeJsonLd);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <HeroSection />
        <FeaturesSection />
        <NewsSection />
        <ProductsSection />
        <TestimonialsSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;